import React from 'react'
import {submitRefTableData, insertNewRefTableData, visualizeWithFilters, visualizeLoad} from "../api/api";
import {Form} from "@q4us-sw/q4us-ui";
import {getFeConfig, visualize} from "../api/api";
import {Button, Modal, notification} from 'antd'
import {
    AppstoreAddOutlined, RedoOutlined,
} from "@ant-design/icons"
import {TableContext, TableOperation, TableV2} from "@q4us-sw/q4us-ui/lib/TableV2";
import {unstable_batchedUpdates} from "react-dom";
import {Header} from "./generic/Header";
import PropTypes from 'prop-types';
import {MapToFilter} from "../utils/mappings";
import {TableFilter} from "./filters/TableFilter";

const {CURRENCY_CODE_EUR} = require('../utils/constants')

export class Validator {
    constructor(name, fn, deps = []) {
        this.name = name;
        this.fn = fn;
        this.deps = deps
    }

    async apply(value, column, deps) {
        if (this.fn)
            return this.fn(value, column, deps)
        else
            return Promise.resolve();
    }
}

export class Visibility{
    constructor(name, fn, deps = []) {
        this.name = name;
        this.fn = fn;
        this.deps = deps
    }

    apply(column, deps) {
        if (this.fn)
            return this.fn(column, deps)
        else
            return true;
    }
}


export class Transform {
    constructor(name, value) {
        this.name = name;
        this.value = value;
    }

    apply(row, column) {
        let _v = this.value;
        if (this.value instanceof Function) {
            _v = this.value.call(row)
        }
        return {...row, [this.name]: _v}
    }
}


export const RefTable = ({
                             tableName,
                             tableSchema,
                             edit,
                             filterSchema,
                             title,
                             initialValue,
                             validator /*Validator*/,
                             transform /*Transform*/,
                             add,
                             rowKey,
                             onEvent,
                            ...props
                         }) => {
    const [open, setOpen] = React.useState(false);
    //
     const load = React.useCallback(async (filters, page)=>{
         return await props.fetch(tableName, filters, page);
     }, [props, tableName])


    return <React.Fragment>
        <TableV2
            tableName={tableName}
            schema={tableSchema}
            shouldRefresh={(operation, status) => {
                return operation === TableOperation.Update
            }}
            customRenderer={props.customRenderer}
            fetchConfig={async (key) => {
                const {data = {}} = await getFeConfig(key);
                const {statusCode, rows = [], fields = []} = data;
                return {data: {statusCode, rows: await props.enrichColumns(rows), fields}};
            }}
            action={edit && {
                name: 'id',
                title: 'Action',
                options: TableOperation.Update,
            }}
            rowKey={rowKey}
            scroll={{y: `calc(100vh - ${filterSchema ? props.headerChildHeight : 0}px - 134px - ${(props.theadHeight + props.tfootHeight)}px)`, x: 'max-content'}}
            fetchData={async (request) => {
                return load(request.options.filter || initialValue || {}, request.page);
            }}
            saveData={async (data) => {
                const status = Object.entries(validator || {}).map(([key,value])=>{
                    return value.apply(data[key], {name: key}, data )
                })
                try{
                    await Promise.all(status)
                }catch (e){
                    notification.error({message: e})
                    return false;
                }
                let t = transform?.apply(data) || data
                try{
                    await props.updateData(tableName, t)
                }catch(e){
                    console.error(e)
                    return false;
                }
                return t;
            }}
            header={
                <Header title={title} actions={<div className={`action-items`}>
                    {props.headerOrder.map(o=>{
                        switch (o) {
                            case "add":
                                return <Button key={o} hidden={!add} style={{width: 160}} icon={<AppstoreAddOutlined/>} onClick={() => setOpen(true)}>Add New Record</Button>
                            case "actions":
                                return props.actions.map(({event, title, icon})=><Button icon={icon} key={event} onClick={()=>onEvent(event)}>{title}</Button>)
                            case "customActionElements":
                                return props.customActionElements
                            default:
                                return undefined;
                        }
                    })}
                </div>} childHeight={props.headerChildHeight}>
                    {filterSchema && <TableFilter schema={filterSchema} initialValue={initialValue} fetch={load}/>}
                </Header>

            }
        >
            {props.children}
        </TableV2>

        <Modal
            centered
            closable={true}
            onCancel={() => setOpen(false)}
            footer={null}
            title={`Add new Record`}
            visible={open}
        >
            <Form maxHeight={`60vh`} schema={props.addNewRecordSchema || tableSchema} config={async (schema) => {
                const res = await getFeConfig(schema) || {}
                const {data: {rows = []} = {}} = res;
                return props.enrichForm(rows)
            }}
                  validateField={async (rule, value, schema, column, form) => {

                      const {[column.name]: _v} = validator || {};
                      if(_v){
                          return _v.apply(value, column, form.getFieldsValue(_v.deps))
                      }
                      return Promise.resolve()
                  }}
                  visibilityValidator={(schema, column, form) => {
                      const {[column.name]: _v} =  props.formVisibility || {}
                      if(_v){
                          return _v.apply( column, form.getFieldsValue(_v.deps))
                      }
                      return true;
                  }}
                  submit={async (values) => {
                      const {data} = await props.insertData(tableName, values);
                      if (data.statusCode === 200) {
                          notification.success({message: `New record added to ${title || tableName}`})
                      }
                      return true;
                  }}/>
        </Modal>


    </React.Fragment>
}

RefTable.propTypes = {
    tableName: PropTypes.string,
    tableSchema: PropTypes.string,
    edit: PropTypes.bool,
    filterSchema: PropTypes.string,
    addNewRecordSchema: PropTypes.string,
    title: PropTypes.string,
    initialValue: PropTypes.object,
    validator: PropTypes.objectOf(PropTypes.instanceOf(Validator)) /*Validator*/,
    formVisibility: PropTypes.objectOf(PropTypes.instanceOf(Visibility)) /*Visibility*/,
    transform: PropTypes.objectOf(PropTypes.instanceOf(Transform)) /*Transform*/,
    actions: PropTypes.arrayOf(PropTypes.shape({event: PropTypes.oneOfType(PropTypes.string, PropTypes.number), title: PropTypes.string, icon: PropTypes.any})),
    customActionElements: PropTypes.arrayOf(PropTypes.element),
    headerOrder: PropTypes.arrayOf(PropTypes.oneOf(["add","actions", "customActionElements"])),
    add: PropTypes.bool,
    rowKey: PropTypes.string,
    onEvent: PropTypes.func,
    headerChildHeight: PropTypes.number,
    theadHeight: PropTypes.number,
    tfootHeight: PropTypes.number,
    updateData: PropTypes.func,
    insertData: PropTypes.func,
    enrichColumns: PropTypes.func,
    enrichForm: PropTypes.func,
    fetch: PropTypes.func,
    customRenderer: PropTypes.object,
}

export const PaginatedLoad = async (tableName, filters = {}, page = {})=>{
    const _filters = MapToFilter(filters)
    return await visualizeLoad({
        tableName: tableName,
        orderBy: ['id desc'],
        filter:[..._filters],
        page: { size: page.size || 100, from: ((page.current || 1) - 1) * (page.size || 100)}
    }) || {}
}


export const Refresh = ()=>{
    return <TableContext.Consumer>
        {
            table =><Button style={{width: 150}} icon={<RedoOutlined/>} onClick={async ()=>{
                table?.setLoading(true)
                const {data : _data} = await table?.props.fetchData({schema: table?.props.schema, page: {}, options: table?.customData})
                unstable_batchedUpdates(()=>{
                    table?.setData(_data?.rows || [] )
                    table?.setLoading(false)
                    table?.setPage(_data?.page || {})
                })
            }}>Refresh</Button>
        }
    </TableContext.Consumer>
}


export const DefaultProps = {
    add: true,
    initialValue: {},
    onEvent: async (e)=>{},
    rowKey: "id",
    actions: [],
    headerChildHeight: 80,
    theadHeight: 40,
    tfootHeight: 0,
    updateData: async (tableName, data)=>submitRefTableData(data, tableName),
    insertData: async (tableName, data)=>insertNewRefTableData(data, tableName),
    customActionElements: [],
    headerOrder:["add","actions", "customActionElements"],
    enrichColumns: (columns) => columns,
    enrichForm: (columns) => columns.filter(({generated}) => !generated).sort(({sequence_no: a}, {sequence_no: b}) =>  a - b),
    fetch: async (tableName, filters = {}, page = {})=>{
        const _filters = MapToFilter(filters, "key")
        if (_filters.length === 0) {
            return await visualize(tableName)
        } else {
            return await visualizeWithFilters(tableName, _filters)
        }
    }
}


RefTable.defaultProps = DefaultProps;
